import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { bankAPI } from '../../utils/api';
import { ArrowLeft, Search, X, ChevronRight } from 'lucide-react';
import './BankSelectionPage.css';

const BankSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const searchInputRef = useRef(null);

  const [banks, setBanks] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [showBranchSelection, setShowBranchSelection] = useState(false);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [branchSearchQuery, setBranchSearchQuery] = useState('');

  // Get current selection from location state
  const currentBankId = location.state?.bankId || null;
  const currentBranchId = location.state?.branchId || null;
  const selectBranchOnly = location.state?.selectBranchOnly || false;

  useEffect(() => {
    fetchBanks();

    // If selectBranchOnly is true and bankId exists, auto-load branches
    if (selectBranchOnly && currentBankId) {
      setSelectedBankId(currentBankId);
      fetchBranches(currentBankId).then(() => {
        setShowBranchSelection(true);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Auto-focus search input
    if (searchInputRef.current && !showBranchSelection) {
      searchInputRef.current.focus();
    }
  }, [showBranchSelection]);

  useEffect(() => {
    // Filter banks based on search query
    if (searchQuery.trim() === '') {
      setFilteredBanks(banks);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = banks.filter(
        (bank) =>
          bank.name?.toLowerCase().includes(query) ||
          bank.fullName?.toLowerCase().includes(query) ||
          bank.nameEn?.toLowerCase().includes(query)
      );
      setFilteredBanks(filtered);
    }
  }, [searchQuery, banks]);

  useEffect(() => {
    // Filter branches based on search query
    if (branchSearchQuery.trim() === '') {
      setFilteredBranches(allBranches);
    } else {
      const query = branchSearchQuery.toLowerCase();
      const filtered = allBranches.filter(
        (branch) =>
          branch.name?.toLowerCase().includes(query) ||
          branch.address?.toLowerCase().includes(query)
      );
      setFilteredBranches(filtered);
    }
  }, [branchSearchQuery, allBranches]);

  const fetchBanks = async () => {
    try {
      const response = await bankAPI.getAll();
      setBanks(response.data.banks || []);
      setFilteredBanks(response.data.banks || []);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
      // Fallback to default banks
      const fallbackBanks = [
        { id: 1, name: '전북은행', fullName: 'Jeonbuk Bank (전북은행)', nameEn: 'Jeonbuk Bank' },
        { id: 2, name: '광주은행', fullName: 'Kwangju Bank (광주은행)', nameEn: 'Kwangju Bank' },
        { id: 3, name: '대부업', fullName: 'Lending Company (대부업)', nameEn: 'Lending Company' },
      ];
      setBanks(fallbackBanks);
      setFilteredBanks(fallbackBanks);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async (bankId) => {
    try {
      const response = await bankAPI.getBranches(bankId);
      setAllBranches(response.data.branches || []);
      setFilteredBranches(response.data.branches || []);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      setAllBranches([]);
      setFilteredBranches([]);
    }
  };

  const handleBankSelect = async (bank) => {
    setSelectedBankId(bank.id);

    // Fetch branches for this bank
    try {
      const response = await bankAPI.getBranches(bank.id);
      const branches = response.data.branches || [];
      setAllBranches(branches);
      setFilteredBranches(branches);

      if (branches.length > 0) {
        // Show branch selection
        setShowBranchSelection(true);
        setBranchSearchQuery('');
      } else {
        // No branches, save to sessionStorage and navigate back
        sessionStorage.setItem('selectedBankId', bank.id.toString());
        sessionStorage.removeItem('selectedBranchId');
        navigate(-1);
      }
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      // No branches available, just select bank
      sessionStorage.setItem('selectedBankId', bank.id.toString());
      sessionStorage.removeItem('selectedBranchId');
      navigate(-1);
    }
  };

  const handleBranchSelect = (branch) => {
    // Save to sessionStorage and navigate back
    sessionStorage.setItem('selectedBankId', selectedBankId.toString());
    sessionStorage.setItem('selectedBranchId', branch.id.toString());
    navigate(-1);
  };

  const handleSkipBranch = () => {
    // Save bank only to sessionStorage and navigate back
    sessionStorage.setItem('selectedBankId', selectedBankId.toString());
    sessionStorage.removeItem('selectedBranchId');
    navigate(-1);
  };

  const handleBackFromBranch = () => {
    setShowBranchSelection(false);
    setSelectedBankId(null);
    setAllBranches([]);
    setFilteredBranches([]);
    setBranchSearchQuery('');
  };

  if (loading) {
    return (
      <div className="bank-selection-page loading">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="bank-selection-page">
      {/* Header */}
      <div className="page-header">
        <button
          className="btn-back"
          onClick={showBranchSelection ? handleBackFromBranch : () => navigate(-1)}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="page-title">
          {showBranchSelection ? t('selectBranch') : t('selectBank')}
        </h1>
      </div>

      {!showBranchSelection ? (
        // Bank Selection View
        <>
          {/* Search Box */}
          <div className="search-container">
            <div className="search-box">
              <Search size={18} className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={t('searchBank')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => setSearchQuery('')}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Banks List */}
          <div className="list-container">
            {filteredBanks.length === 0 ? (
              <div className="empty-state">
                <p>{t('noBanksFound')}</p>
              </div>
            ) : (
              <div className="items-list">
                {filteredBanks.map((bank) => (
                  <button
                    key={bank.id}
                    className={`list-item ${currentBankId === bank.id ? 'selected' : ''}`}
                    onClick={() => handleBankSelect(bank)}
                  >
                    <div className="item-content">
                      <div className="item-name">{bank.name}</div>
                      {bank.fullName && (
                        <div className="item-description">{bank.fullName}</div>
                      )}
                    </div>
                    <ChevronRight size={20} className="item-arrow" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        // Branch Selection View
        <>
          {/* Search Box */}
          <div className="search-container">
            <div className="search-box">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder={t('searchBranch')}
                value={branchSearchQuery}
                onChange={(e) => setBranchSearchQuery(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                autoFocus
              />
              {branchSearchQuery && (
                <button
                  className="search-clear"
                  onClick={() => setBranchSearchQuery('')}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Skip Branch Button */}
            <button className="btn-skip" onClick={handleSkipBranch}>
              {t('skipBranchSelection')}
            </button>
          </div>

          {/* Branches List */}
          <div className="list-container">
            {filteredBranches.length === 0 ? (
              <div className="empty-state">
                <p>{t('noBranchesFound')}</p>
              </div>
            ) : (
              <div className="items-list">
                {filteredBranches.map((branch) => (
                  <button
                    key={branch.id}
                    className={`list-item ${currentBranchId === branch.id ? 'selected' : ''}`}
                    onClick={() => handleBranchSelect(branch)}
                  >
                    <div className="item-content">
                      <div className="item-name">{branch.name}</div>
                      {branch.address && (
                        <div className="item-description">{branch.address}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BankSelectionPage;
